import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Layout";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const activeCat = params.get("category") || "";

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (activeCat) q.category = activeCat;
    if (search) q.search = search;
    const t = setTimeout(() => {
      api.get("/products", { params: q }).then(({ data }) => {
        setProducts(data);
        setLoading(false);
      });
    }, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [activeCat, search]);

  const setCat = (slug) => {
    const p = new URLSearchParams(params);
    if (slug) p.set("category", slug); else p.delete("category");
    setParams(p);
  };

  const catName = categories.find((c) => c.slug === activeCat)?.name || "All Products";

  return (
    <div className="pt-28 pb-10 max-w-[1600px] mx-auto px-6 md:px-10">
      <Seo
        title={`${catName} — Indoforce Store`}
        description={`Browse ${catName.toLowerCase()} — air compressors, car wash machines, spares and accessories at trade prices.`}
        canonical={`${window.location.origin}/shop`}
      />
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-[#E31E24] mb-4">Catalog</p>
        <h1 className="font-display text-5xl md:text-7xl font-black uppercase tracking-tighter leading-none">{catName}</h1>
      </Reveal>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mt-12 mb-10 border-y border-white/10 py-6">
        <div className="flex flex-wrap gap-2" data-testid="category-filters">
          <button
            onClick={() => setCat("")}
            data-testid="filter-all"
            className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${!activeCat ? "bg-[#E31E24] text-black border-[#E31E24]" : "border-white/20 text-white/70 hover:border-[#E31E24]"}`}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              onClick={() => setCat(c.slug)}
              data-testid={`filter-${c.slug}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${activeCat === c.slug ? "bg-[#E31E24] text-black border-[#E31E24]" : "border-white/20 text-white/70 hover:border-[#E31E24]"}`}
            >
              {c.name}
            </button>
          ))}
        </div>
        <div className="relative w-full lg:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full bg-transparent border border-white/20 focus:border-[#E31E24] outline-none pl-9 pr-4 py-3 text-sm placeholder:text-white/30"
          />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <p className="text-white/50 py-20 text-center" data-testid="no-products">No products found.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="product-grid">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
